import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from 'v-select2-multiple-component';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ValidationService from "@/services/ValidationService";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import CONFIG from "@/assets/js/config.custom.js";

// Same as Excel template column C: "Default,Enrollment" (backend import allows types 2 and 3 only).
const ALLOWED_ITEM_TYPES = Object.keys(CONFIG.PAYMENT_ITEM_TYPES).map((id) => Number(id));

export default {
  name: "ItemImport",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },
  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      acdYearList: [],
      templateAcdYearId: null,
      acdYearId: null,
      file_name: '',
      invalidFileType: false,
      invalidFileSize: false,
      submitted: false,
      templateSubmitted: false,
      disableImportBtn: false,
      showPreview: false,
      previewData: [],
      previewSummary: {},
      isSaving: false,
      gradeArr: [],
      transactionCategoryArr: [],
      CONFIG
    };
  },

  computed: {
    disabledTemplateBtn() {
      return false;
    },

    itemTypeOptions() {
      return this.CONFIG.PAYMENT_ITEM_TYPES || {};
    },

    selectedAcademicYearName() {
      const selected = this.acdYearList.find(
        (period) => String(period.id) === String(this.acdYearId)
      );

      return selected?.text || this.previewData[0]?.academic_year_name || "";
    },
  },

  validations: {
    acdYearId: { required },
    file_name: { required },
  },

  watch: {
    acdYearId() {
      if (this.showPreview) {
        this.clearPreview();
      }
    },
  },

  methods: {
    async getAcademicYearList() {
      try {
        this.isLoading = true;
        const response = await axios.get("period/periodList");
        if (response.data && response.data.data) {
          this.acdYearList = response.data.data;
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    getTemplateFileName() {
      const selected = this.acdYearList.find(
        (period) => String(period.id) === String(this.templateAcdYearId)
      );
      const periodName = (selected?.text || "all").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_items_${year}_${month}_${day}.xlsx`;
    },

    async templateDownload() {
      this.templateSubmitted = true;

      if (this.showPreview) {
        return;
      }

      const params = {};
      if (this.templateAcdYearId) {
        params.acd_year_id = this.templateAcdYearId;
      }

      this.isLoading = true;

      try {
        const response = await axios({
          method: 'get',
          url: `/item/export`,
          params,
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = this.getTemplateFileName();
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        if (error.response && error.response.status === 500) {
          const blob = error.response.data;
          const text = await blob.text();
          try {
            const json = JSON.parse(text);
            this.$notification.error(json.message || "Failed to download template.", {
              timer: 3, position: "bottomCenter",
            });
          } catch {
            this.$notification.error("Failed to download template.", {
              timer: 3, position: "bottomCenter",
            });
          }
        } else {
          this.handleError(error);
        }
      } finally {
        this.isLoading = false;
      }
    },

    chooseFile() {
      if (this.showPreview) return;
      this.$refs.fileUploader.value = null;
      this.file_name = '';
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const allowedTypes = ['.xlsx', '.xls'];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(`.${ext}`)) {
        this.invalidFileType = true;
        this.file_name = '';
        return;
      }
      this.invalidFileType = false;
      this.file_name = file.name;
    },

    async confirmImport() {
      this.submitted = true;
      if (this.$v.$invalid) return;

      const file = this.$refs.fileUploader.files[0];
      if (!file) {
        this.invalidFileType = true;
        this.file_name = '';
        return;
      }
      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        this.invalidFileSize = true;
        return;
      }
      this.invalidFileSize = false;
      this.validationErrors = {};
      this.clearValidationErrors();
      await this.doValidate();
    },

    async doValidate() {
      this.isLoading = true;
      this.disableImportBtn = true;

      const formData = new FormData();
      formData.append('target_academic_year_id', this.acdYearId);
      formData.append('file', this.$refs.fileUploader.files[0]);

      try {
        const response = await axios.post('/item/import/validate', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data && response.data.success) {
          await this.loadPreviewDropdownOptions();
          this.previewData = this.clonePreviewItems(response.data.preview.items);
          this.previewSummary = response.data.preview.summary;
          this.clearImportConfirmErrors();
          this.showPreview = true;
        }
      } catch (error) {
        this.handleImportValidationError(error);
      } finally {
        this.disableImportBtn = false;
        this.isLoading = false;
      }
    },

    async doImport() {
      this.clearImportConfirmErrors();

      if (!this.previewData.length) {
        alert("No data to import.");
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isSaving = true;
      this.isLoading = true;

      try {
        const response = await axios.post('/item/import', {
          target_academic_year_id: this.acdYearId,
          created_id: this.$store.state.user.id,
          items: JSON.stringify(this.buildConfirmPayload()),
        });
        if (response.data && response.data.success) {
          this.$notification.success("Payment items imported successfully.", {
            timer: 3, position: "bottomCenter",
          });
          this.showList();
        }
      } catch (error) {
        this.handleConfirmBackendError(error);
      } finally {
        this.isSaving = false;
        this.isLoading = false;
      }
    },

    async loadPreviewDropdownOptions() {
      await Promise.all([
        this.loadGradeOptions(),
        this.loadTransactionCategoryOptions(),
      ]);
    },

    async loadGradeOptions() {
      if (!this.acdYearId) {
        this.gradeArr = [];
        return;
      }

      try {
        const response = await axios.get('campusGrade/list-by-acdYear/', {
          params: {
            campus_id: this.$store.state.user.campus_id,
            acd_year_id: this.acdYearId,
            get_grade: true,
          },
        });
        this.gradeArr = (response.data || []).map((grade) => ({
          id: grade.campus_grade_id,
          text: grade.grade_name,
        }));
      } catch (error) {
        this.handleError(error);
        this.gradeArr = [];
      }
    },

    async loadTransactionCategoryOptions() {
      try {
        const response = await axios.get(
          `/transactionCategory/getTransactionCategoryArray?type=${this.CONFIG.TRANSACTION_TYPE.INCOME}`
        );
        const categoryList = Array.isArray(response.data?.[0]) ? response.data[0] : [];
        this.transactionCategoryArr = [
          { id: 0, text: 'Select Transaction Category' },
          ...categoryList.map((category) => ({
            id: category.id,
            text: category.text ?? category.category_name ?? "",
          })),
        ];
      } catch (error) {
        this.handleError(error);
        this.transactionCategoryArr = [{ id: 0, text: 'Select Transaction Category' }];
      }
    },

    normalizeCampusGradeIds(value) {
      if (Array.isArray(value)) {
        return value.map((id) => String(id)).filter((id) => id !== "");
      }

      return String(value || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    },

    normalizeTransactionCategoryId(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      return String(value);
    },

    gradeNamesFromIds(ids) {
      const idList = this.normalizeCampusGradeIds(ids);

      return idList
        .map((id) => this.gradeArr.find((grade) => String(grade.id) === String(id))?.text)
        .filter(Boolean);
    },

    categoryNameFromId(id) {
      const categoryId = this.normalizeTransactionCategoryId(id);
      if (!categoryId || categoryId === "0") {
        return "";
      }

      return this.transactionCategoryArr.find(
        (category) => String(category.id) === categoryId
      )?.text || "";
    },

    clonePreviewItems(items) {
      return (items || []).map((item) => ({
        row_num: item.row_num,
        p_item_name: String(item.p_item_name || "").trim(),
        p_item_amount: item.p_item_amount,
        item_type: item.item_type,
        grade_display_names: [...(item.grade_display_names || [])],
        campus_grade_ids: this.resolveCampusGradeIds(item),
        transaction_category_id: this.resolveTransactionCategoryId(item),
        transaction_category_name: String(item.transaction_category_name || "").trim(),
      }));
    },

    resolveTransactionCategoryId(item) {
      const apiId = this.normalizeTransactionCategoryId(item.transaction_category_id);
      if (apiId && apiId !== "0") {
        return apiId;
      }

      // Fall back to matching by category name (case-insensitive) so the dropdown
      // still selects the right category even if the backend id is missing.
      const name = String(item.transaction_category_name || "").trim().toLowerCase();
      if (name) {
        const match = this.transactionCategoryArr.find(
          (category) => String(category.id) !== "0"
            && String(category.text || "").trim().toLowerCase() === name
        );
        if (match) {
          return String(match.id);
        }
      }

      return apiId;
    },

    resolveCampusGradeIds(item) {
      // Prefer matching by display name so the multi-select shows every grade even if
      // the backend IDs and the campus-filtered dropdown options ever drift apart.
      const displayNames = item.grade_display_names || [];
      if (displayNames.length) {
        const matchedIds = displayNames
          .map((name) => this.gradeArr.find((grade) => grade.text === name)?.id)
          .filter((id) => id !== undefined && id !== null)
          .map((id) => String(id));

        if (matchedIds.length === displayNames.length) {
          return matchedIds;
        }
      }

      return this.normalizeCampusGradeIds(item.campus_grade_ids);
    },

    buildConfirmPayload() {
      return this.previewData.map((item, index) => ({
        row_num: item.row_num || (index + 2),
        p_item_name: String(item.p_item_name || "").trim(),
        p_item_amount: this.normalizeAmount(item.p_item_amount),
        item_type: parseInt(item.item_type, 10),
        grade_display_names: this.gradeNamesFromIds(item.campus_grade_ids),
        transaction_category_name: this.categoryNameFromId(item.transaction_category_id),
      }));
    },

    normalizeAmount(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      const clean = String(value).replace(/,/g, "").trim();
      if (!/^\d+$/.test(clean)) {
        return null;
      }

      const amount = parseInt(clean, 10);
      if (amount < 1 || amount > 999999999) {
        return null;
      }

      return amount;
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const seenItems = {};

      this.previewData.forEach((item, index) => {
        const rowErrors = {};
        const itemName = String(item.p_item_name || "").trim();
        const amount = this.normalizeAmount(item.p_item_amount);
        const itemType = parseInt(item.item_type, 10);

        if (!itemName) {
          rowErrors.p_item_name = "Item name is required.";
        } else if (itemName.length > 100) {
          rowErrors.p_item_name = "Max 100 characters.";
        }

        if (item.p_item_amount === null || item.p_item_amount === undefined || String(item.p_item_amount).trim() === "") {
          rowErrors.p_item_amount = "Amount is required.";
        } else if (amount === null) {
          rowErrors.p_item_amount = "Invalid amount.";
        }

        if (!itemType) {
          rowErrors.item_type = "Item type is required.";
        } else if (!ALLOWED_ITEM_TYPES.includes(itemType)) {
          rowErrors.item_type = "Invalid item type.";
        }

        const gradeIds = this.normalizeCampusGradeIds(item.campus_grade_ids);
        if (gradeIds.length) {
          const invalidGrade = gradeIds.some(
            (id) => !this.gradeArr.some((grade) => String(grade.id) === String(id))
          );
          if (invalidGrade) {
            rowErrors.campus_grade_ids = "Invalid grade selection.";
          } else if (gradeIds.join(",").length > 2000) {
            rowErrors.campus_grade_ids = "Max grades limit exceeded.";
          }
        }

        const categoryId = this.normalizeTransactionCategoryId(item.transaction_category_id);
        if (categoryId && categoryId !== "0") {
          const categoryExists = this.transactionCategoryArr.some(
            (category) => String(category.id) === categoryId && String(category.id) !== "0"
          );
          if (!categoryExists) {
            rowErrors.transaction_category_id = "Invalid transaction category.";
          }
        }

        if (!Object.keys(rowErrors).length && itemName && amount !== null && itemType) {
          const itemKey = `${itemName.toLowerCase()}|${itemType}`;
          if (seenItems[itemKey]) {
            rowErrors.p_item_name = "Duplicate item with this type.";
          } else {
            seenItems[itemKey] = true;
          }
        }

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    clearPreview() {
      this.showPreview = false;
      this.previewData = [];
      this.previewSummary = {};
      this.gradeArr = [];
      this.transactionCategoryArr = [];
      this.clearImportConfirmErrors();
      this.file_name = '';
      this.submitted = false;
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
    },

    resetAll() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.templateAcdYearId = null;
      this.acdYearId = null;
      this.file_name = '';
      this.invalidFileType = false;
      this.invalidFileSize = false;
      this.validationErrors = {};
      this.clearPreview();
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYearList();
    },

    showList() {
      this.$router.push({ name: 'itemList' });
    },

    getItemTypeLabel(type) {
      return this.itemTypeOptions[type] || "Unknown";
    },

    handleImportValidationError(error) {
      if (error.response && error.response.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.msg || data.message || "Validation failed."];

        window.alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
      this.$notification.error("Validation failed.", {
        timer: 3, position: "bottomCenter",
      });
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }

      if (error.response && error.response.status === 500) {
        alert("Import failed. All changes rolled back.");
        return;
      }

      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return {
        payloadPrefixes: ["items"],
      };
    },

    handleError(error) {
      if (error.response && error.response.status === 401) {
        this.$store.commit('logout');
        window.location.replace('login');
      } else {
        console.error(error);
      }
    },
  },

  mounted() {
    this.getAcademicYearList();
  },
};
